import { api } from "./client";

export interface SchemaContextMetric {
  key: string;
  label: string;
  category: string;
  description: string;
  measureField: string;
  query: {
    model: string;
    explore: string;
    fields: string[];
    filters?: Record<string, string>;
    sorts?: string[];
    limit?: number;
  };
}

export interface SchemaContextField {
  name: string;
  business_definition: string;
  unit_of_measure: string;
  snowflake_table: string;
  snowflake_column: string;
  category: string;
}

export interface AnalyticsSchemaResponse {
  metrics: SchemaContextMetric[];
  fields: SchemaContextField[];
  metricCount: number;
  fieldCount: number;
}

export interface SavedExploration {
  id: string;
  companyId: string;
  createdByUserId: string;
  title: string;
  description: string | null;
  lookerQuery: SchemaContextMetric["query"];
  vizType: string;
  config: Record<string, unknown>;
  lastQuestion: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NlqResultPayload {
  conversationId: string;
  rationale: string;
  lookerQuery: SchemaContextMetric["query"];
  viz: string;
  followUpSuggestions: string[];
  rows: Record<string, unknown>[];
  totalRows: number;
  fields: { name: string; label: string; type: string }[];
  analysis: string;
  queryTimeMs: number;
}

export type StreamEvent =
  | { event: "start"; data: { conversationId: string } }
  | { event: "schema_loaded"; data: { metricCount: number; fieldCount: number } }
  | { event: "query"; data: { rationale: string; lookerQuery: SchemaContextMetric["query"]; viz: string; followUpSuggestions: string[] } }
  | { event: "result"; data: { rows: Record<string, unknown>[]; totalRows: number; fields: { name: string; label: string; type: string }[]; queryTimeMs: number } }
  | { event: "analysis_delta"; data: { text: string } }
  | { event: "done"; data: { conversationId: string; totalRows: number } }
  | { event: "error"; data: { code: string; message: string } };

export const analyticsStudioApi = {
  schema: (companyId: string) =>
    api.get<AnalyticsSchemaResponse>(`/companies/${companyId}/analytics-studio/schema`),

  nlq: (companyId: string, question: string, conversationId?: string) =>
    api.post<NlqResultPayload>(`/companies/${companyId}/analytics-studio/nlq`, {
      question,
      conversationId,
    }),

  /** Run a Looker query directly — no LLM query-generation step. Still runs
   * the result-analysis pass on the rows. Used by the "Run" buttons on the
   * schema browser + the "reload saved" path. */
  runDirect: (companyId: string, lookerQuery: SchemaContextMetric["query"]) =>
    api.post<NlqResultPayload>(`/companies/${companyId}/analytics-studio/run`, {
      lookerQuery,
    }),

  /**
   * Streaming variant. Yields events as they arrive over SSE. Uses `fetch`
   * + manual chunk decoding instead of EventSource because EventSource
   * doesn't support POST bodies in browsers.
   */
  nlqStream: async function* (
    companyId: string,
    question: string,
    conversationId?: string,
  ): AsyncGenerator<StreamEvent> {
    const res = await fetch(`/api/companies/${companyId}/analytics-studio/nlq-stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({ question, conversationId }),
    });
    if (!res.ok || !res.body) {
      const err = await res.text();
      throw new Error(`nlq-stream failed (${res.status}): ${err}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 2);
        const lines = block.split("\n");
        let eventName = "message";
        let dataStr = "";
        for (const line of lines) {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataStr += line.slice(5).trim();
          }
        }
        if (dataStr) {
          try {
            const parsed = JSON.parse(dataStr);
            yield { event: eventName, data: parsed } as StreamEvent;
          } catch {
            // skip malformed chunk
          }
        }
      }
    }
  },

  listSaved: (companyId: string) =>
    api.get<SavedExploration[]>(`/companies/${companyId}/analytics-studio/saved`),

  save: (
    companyId: string,
    body: {
      title: string;
      description?: string;
      lookerQuery: SchemaContextMetric["query"];
      vizType: string;
      config?: Record<string, unknown>;
      lastQuestion?: string;
    },
  ) => api.post<SavedExploration>(`/companies/${companyId}/analytics-studio/saved`, body),

  delete: (companyId: string, id: string) =>
    api.delete<{ deleted: number }>(`/companies/${companyId}/analytics-studio/saved/${id}`),

  promoteToWidget: (companyId: string, id: string, dashboardId: string) =>
    api.post<{ widgetId: string; dashboardId: string }>(
      `/companies/${companyId}/analytics-studio/saved/${id}/promote-to-widget`,
      { dashboardId },
    ),
};
